import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Award, MapPin, Facebook, Instagram, Home, Building2, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { COMPANY_INFO, SOCIAL_MEDIA, LOGO_URL } from '../data/mockData';
import LeadCaptureForm from '../components/forms/LeadCaptureForm';
import ClientReviewsCarousel from '../components/ClientReviewsCarousel';
import MyActiveListings from '../components/MyActiveListings';

const ALEXANDER = {
  name: 'Alexander Conti',
  role: 'Sales Representative — REALTOR®',
  photo: '/brand/alexander.jpeg',
  phone: COMPANY_INFO.phone,
  email: COMPANY_INFO.email,
  experience: '8+ years',
  languages: ['English'],
  specialties: [
    'Residential Home Sales',
    'Multi-Residential',
    'Commercial Real Estate',
    'Investment Properties',
    'Buyer Representation',
    'First Time Buyers',
    'Vacant Land',
    'Condominiums',
  ],
};

const CAREER_AWARDS = [
  { name: 'Directors Platinum Award', years: '2020, 2022, 2023, 2025' },
  { name: "President's Gold Award", years: '2020 – 2025' },
  { name: 'Master Sales Award', years: '2020 – 2025' },
];

const BIO_PARAGRAPHS = [
  `With 8 years of hands-on real estate experience, I've built my career on a simple premise: real estate should be transparent, streamlined, and stress-free.`,
  `Having successfully created and managed my own real estate team, I have now shifted my primary focus back to working directly with my clients one-on-one. Over the years, I've navigated virtually every type of transaction across the region — from residential home sales and multi-residential properties to commercial spaces and strategic real estate investments.`,
  `My approach is deeply task-oriented and process-driven. I take the complexity out of buying and selling by managing every detail with precision, ensuring your transaction moves forward seamlessly from start to finish. Behind every deal is deep local market knowledge, direct communication, and an uncompromised commitment to putting your best interests first.`,
  `Whether you're stepping onto the property ladder, expanding your investment portfolio, or making a strategic commercial move, you get an experienced advocate focused entirely on your success.`,
];

const TeamPage = () => {
  return (
    <div className="min-h-screen bg-gray-50" data-testid="about-me-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-950 to-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_25%_20%,rgba(220,38,38,0.35),transparent_55%)]" />
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_80%_80%,rgba(220,38,38,0.25),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24 grid md:grid-cols-5 gap-10 items-center">
          {/* Left: name + tagline + CTAs */}
          <div className="md:col-span-3">
            <p className="text-red-500 uppercase tracking-widest text-xs md:text-sm mb-4 font-semibold" data-testid="about-eyebrow">
              About Me
            </p>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              Alexander <span className="text-red-500">Conti</span>
            </h1>
            <p className="text-lg md:text-xl italic text-gray-300 mb-6" data-testid="about-tagline">
              Your Vision, My Mission.
            </p>
            <p className="text-gray-300 text-base md:text-lg max-w-xl mb-8">
              Sales Representative &middot; REALTOR® &middot; {COMPANY_INFO.brokerage}
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href={`tel:${ALEXANDER.phone}`}
                data-testid="about-call-btn"
                className="inline-flex items-center gap-2 rounded-md bg-red-600 hover:bg-red-700 active:bg-red-800 transition-colors px-6 py-3 text-white font-semibold shadow-lg shadow-red-900/30"
              >
                <Phone className="w-4 h-4" /> {ALEXANDER.phone}
              </a>
              <Link
                to="/contact"
                data-testid="about-email-btn"
                className="inline-flex items-center gap-2 rounded-md border-2 border-red-500/50 hover:border-red-500 hover:bg-red-500/10 transition-colors px-6 py-3 text-white font-semibold"
              >
                <Mail className="w-4 h-4" /> Email me
              </Link>
            </div>
          </div>

          {/* Right: brand card — the AC monogram badge + tagline in a refined dark frame */}
          <div className="md:col-span-2 flex justify-center md:justify-end">
            <div
              data-testid="about-hero-card"
              className="relative w-full max-w-sm rounded-2xl bg-gradient-to-br from-gray-800/60 via-gray-900/60 to-black/80 backdrop-blur border border-white/10 shadow-2xl shadow-red-900/20 p-6 md:p-8"
            >
              {/* Decorative accent line */}
              <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-red-500/60 to-transparent" />
              <div className="flex flex-col items-center text-center">
                <span className="relative h-28 w-28 md:h-32 md:w-32 rounded-full overflow-hidden bg-black ring-2 ring-red-500/30 ring-offset-2 ring-offset-gray-900 mb-5">
                  <img
                    src={LOGO_URL}
                    alt="AC monogram"
                    className="absolute inset-0 h-full w-full object-cover scale-[1.55]"
                  />
                </span>
                <h2 className="text-2xl font-bold tracking-widest text-white">ALEXANDER CONTI</h2>
                <p className="text-red-500 text-sm tracking-[0.25em] mt-1">REALTOR®</p>
                <div className="mt-4 pt-4 border-t border-white/10 w-full">
                  <p className="italic text-gray-400 text-sm tracking-wide">
                    Your Vision, My Mission.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bio + portrait */}
      <section className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-1 flex flex-col items-center md:items-start">
          <img
            src={ALEXANDER.photo}
            alt={ALEXANDER.name}
            className="w-full max-w-xs rounded-2xl shadow-lg object-cover mb-6"
          />
          <div className="w-full space-y-2 text-sm">
            <a
              href={`tel:${ALEXANDER.phone}`}
              className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors"
            >
              <Phone size={16} /> {ALEXANDER.phone}
            </a>
            <Link
              to="/contact"
              className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors break-all"
              data-testid="about-bio-email"
            >
              <Mail size={16} className="flex-shrink-0" />
              <span className="break-all">{ALEXANDER.email}</span>
            </Link>
            <div className="flex items-start gap-2 text-gray-700">
              <MapPin size={16} className="mt-1 flex-shrink-0" /> {COMPANY_INFO.address}
            </div>
            <div className="flex gap-3 pt-3">
              <a
                href={SOCIAL_MEDIA.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-gray-100 rounded-full hover:bg-red-600 hover:text-white transition-colors"
                data-testid="about-facebook"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                href={SOCIAL_MEDIA.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-gray-100 rounded-full hover:bg-red-600 hover:text-white transition-colors"
                data-testid="about-instagram"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900">
            Real estate should be transparent, streamlined, and stress-free.
          </h2>
          <div className="space-y-4 text-gray-700 leading-relaxed text-base md:text-lg">
            {BIO_PARAGRAPHS.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-3 text-gray-900">Areas of Expertise</h3>
            <div className="flex flex-wrap gap-2">
              {ALEXANDER.specialties.map((s) => (
                <Badge
                  key={s}
                  variant="secondary"
                  className="bg-gray-100 text-gray-800 hover:bg-red-600 hover:text-white transition-colors"
                >
                  {s}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      <MyActiveListings />

      {/* Highlights */}
      <section className="bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-14 grid md:grid-cols-3 gap-6">
          <Card className="border-0 shadow-none bg-gray-50">
            <CardContent className="p-6 flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <Home className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Residential Sales</h4>
                <p className="text-sm text-gray-600">
                  Full-service representation for buyers &amp; sellers across the Brantford region.
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-none bg-gray-50">
            <CardContent className="p-6 flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <Building2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Commercial &amp; Multi-Res</h4>
                <p className="text-sm text-gray-600">
                  Strategic transactions for owner-users, tenants, and portfolio holders.
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-none bg-gray-50">
            <CardContent className="p-6 flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Investment Properties</h4>
                <p className="text-sm text-gray-600">
                  Data-driven investment analysis to grow your real estate portfolio.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Client Reviews */}
      <ClientReviewsCarousel />

      {/* Awards */}
      <section className="max-w-7xl mx-auto px-4 py-14">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-2 text-gray-900">Career Recognition</h2>
        <p className="text-center text-gray-500 mb-10">Royal LePage career milestones</p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {CAREER_AWARDS.map((a) => (
            <Card key={a.name} className="border border-gray-200">
              <CardContent className="p-6 text-center">
                <Award className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <p className="font-semibold text-gray-900">{a.name}</p>
                <p className="text-xs text-gray-500 mt-1">{a.years}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">
              Ready to make your <span className="text-red-500">move</span>?
            </h2>
            <p className="text-gray-400">
              Send me a note and I'll be in touch within the hour during business hours.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-2xl">
            <LeadCaptureForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
